import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

const sections = [
  {
    id: 'overview',
    title: '1. Overview',
    content: `EVENTSHOWCASE ("we", "us", or "our") operates the website at eventshowcase.de (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website. Please read this policy carefully. If you disagree with its terms, please discontinue use of the site.

We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the "Last Updated" date of this Privacy Policy. You are encouraged to periodically review this Privacy Policy to stay informed of updates.`,
  },
  {
    id: 'information-collected',
    title: '2. Information We Collect',
    content: `We may collect information about you in a variety of ways. The information we may collect on the Site includes:

**Personal Data**
When you register an account, create or register for events, or subscribe to a plan, we collect personally identifiable information such as your name, email address, and payment information.

**Derivative Data**
Our servers automatically collect information when you access the Site, such as your IP address, browser type, operating system, access times, and the pages you have viewed directly before and after accessing the Site.

**Financial Data**
Financial information, such as data related to your payment method (e.g., valid credit card number, card brand, expiration date) may be collected when you purchase a subscription or register for a paid event. We store only very limited, if any, financial information that we collect. Otherwise, all financial information is stored by our payment processors (Paystack, Flutterwave, PayPal), and you are encouraged to review their privacy policies.

**Event & Registration Data**
When you create or register for events, we collect event details, attendee information, and transaction records necessary to provide the Service.`,
  },
  {
    id: 'google-adsense',
    title: '3. Google AdSense & Advertising',
    content: `We use Google AdSense to display advertisements on our website. Google AdSense is an advertising service provided by Google LLC.

**How Google AdSense Works**
Google AdSense uses cookies and web beacons to serve ads based on your prior visits to our website and other websites on the internet. Google's use of advertising cookies enables it and its partners to serve ads to you based on your visit to our site and/or other sites on the internet.

**Cookies Used by Google AdSense**
Google AdSense sets cookies to:
- Distinguish you from other users
- Remember your preferences and settings
- Understand how you interact with advertisements
- Deliver targeted advertising based on your interests
- Measure the effectiveness of advertising campaigns
- Prevent the same ad from being shown to you too many times

**Third-Party Advertising Partners**
Google and its partners may use cookies, web beacons, and similar technologies to collect or receive information from our website and elsewhere on the internet and use that information to provide measurement services and target ads.

**Opting Out of Personalized Advertising**
You may opt out of personalized advertising by visiting Google's Ads Settings at https://www.google.com/settings/ads or by visiting the Network Advertising Initiative opt-out page at http://www.networkadvertising.org/managing/opt_out.asp.

You can also opt out of a third-party vendor's use of cookies for personalized advertising by visiting www.aboutads.info.

**DoubleClick Cookie**
Google, as a third-party vendor, uses cookies to serve ads on our site. Google's use of the DoubleClick cookie enables it and its partners to serve ads to our users based on their visit to our site and/or other sites on the internet.`,
  },
  {
    id: 'cookies',
    title: '4. Cookies & Tracking Technologies',
    content: `We use cookies, web beacons, tracking pixels, and other tracking technologies on the Site to help customize the Site and improve your experience.

**Types of Cookies We Use**

*Essential Cookies* — These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you such as setting your privacy preferences, logging in, or filling in forms.

*Performance Cookies* — These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us know which pages are the most and least popular.

*Functional Cookies* — These cookies enable the website to provide enhanced functionality and personalisation, such as remembering your login status and preferences.

*Targeting / Advertising Cookies* — These cookies may be set through our site by our advertising partners (including Google AdSense). They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites.

**Managing Cookies**
Most web browsers allow some control of most cookies through the browser settings. To find out more about cookies, including how to see what cookies have been set, visit www.aboutcookies.org or www.allaboutcookies.org.

You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access cookies. If you disable or refuse cookies, please note that some parts of this website may become inaccessible or not function properly.`,
  },
  {
    id: 'use-of-data',
    title: '5. Use of Your Information',
    content: `Having accurate information about you permits us to provide you with a smooth, efficient, and customised experience. Specifically, we may use information collected about you via the Site to:

- Create and manage your account
- Process event registrations and subscription payments
- Send you administrative information, such as information regarding the Site and changes to our terms, conditions, and policies
- Send you marketing and promotional communications (with your consent)
- Respond to your comments, questions, and requests
- Monitor and analyse usage and trends to improve your experience with the Site
- Notify you of updates to the Site
- Perform other business activities as needed
- Prevent fraudulent transactions, monitor against theft, and protect against criminal activity
- Compile anonymous statistical data and analysis for use internally or with third parties
- Deliver targeted advertising, coupons, newsletters, and other information regarding promotions and the Site to you
- Resolve disputes and troubleshoot problems`,
  },
  {
    id: 'disclosure',
    title: '6. Disclosure of Your Information',
    content: `We may share information we have collected about you in certain situations. Your information may be disclosed as follows:

**By Law or to Protect Rights**
If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, and safety of others, we may share your information as permitted or required by any applicable law, rule, or regulation.

**Third-Party Service Providers**
We may share your information with third parties that perform services for us or on our behalf, including payment processing (Paystack, Flutterwave, PayPal), data analysis, email delivery, hosting services, customer service, and marketing assistance.

**Advertising Partners (Google AdSense)**
We share data with Google LLC for the purpose of serving personalised advertisements through Google AdSense. Google may use this data in accordance with its own Privacy Policy available at https://policies.google.com/privacy.

**Business Transfers**
We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.

**With Your Consent**
We may disclose your personal information for any other purpose with your consent.`,
  },
  {
    id: 'data-retention',
    title: '7. Data Retention',
    content: `We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our policies.

Account data is retained for as long as your account is active. If you delete your account, we will delete or anonymise your personal data within 30 days, except where we are required to retain it for legal or regulatory purposes.

Event and transaction records may be retained for up to 7 years for tax and accounting purposes.`,
  },
  {
    id: 'your-rights',
    title: '8. Your Privacy Rights',
    content: `Depending on your location, you may have the following rights regarding your personal data:

**Right to Access** — You have the right to request copies of your personal data.

**Right to Rectification** — You have the right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete.

**Right to Erasure** — You have the right to request that we erase your personal data, under certain conditions.

**Right to Restrict Processing** — You have the right to request that we restrict the processing of your personal data, under certain conditions.

**Right to Data Portability** — You have the right to request that we transfer the data that we have collected to another organisation, or directly to you, under certain conditions.

**Right to Object** — You have the right to object to our processing of your personal data, under certain conditions.

**GDPR (EU/EEA Users)** — If you are located in the European Economic Area, you have additional rights under the General Data Protection Regulation. You may lodge a complaint with your local data protection authority.

**CCPA (California Users)** — California residents have specific rights under the California Consumer Privacy Act, including the right to know what personal information is collected, the right to delete personal information, and the right to opt-out of the sale of personal information.

To exercise any of these rights, please contact us at the details provided in the Contact section below.`,
  },
  {
    id: 'children',
    title: '9. Children\'s Privacy',
    content: `Our Service is not directed to children under the age of 13. We do not knowingly collect personally identifiable information from children under 13. If you are a parent or guardian and you are aware that your child has provided us with personal data, please contact us. If we become aware that we have collected personal data from children without verification of parental consent, we take steps to remove that information from our servers.`,
  },
  {
    id: 'security',
    title: '10. Security of Your Information',
    content: `We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse.

Any information disclosed online is vulnerable to interception and misuse by unauthorised parties. Therefore, we cannot guarantee complete security if you provide personal information.`,
  },
  {
    id: 'third-party-links',
    title: '11. Third-Party Websites',
    content: `The Site may contain links to third-party websites and applications of interest, including advertisements and external services, that are not affiliated with us. Once you have used these links to leave the Site, any information you provide to these third parties is not covered by this Privacy Policy, and we cannot guarantee the safety and privacy of your information.

Before visiting and providing any information to any third-party websites, you should inform yourself of the privacy policies and practices (if any) of the third party responsible for that website, and should take those steps necessary to, in your discretion, protect the privacy of your information.`,
  },
  {
    id: 'contact',
    title: '12. Contact Us',
    content: `If you have questions or comments about this Privacy Policy, please contact us at:

**EVENTSHOWCASE**
Website: https://eventshowcase.de
Email: privacy@eventshowcase.de

For data protection inquiries specifically related to GDPR, you may also contact our Data Protection Officer at the same email address with the subject line "DPO Request".`,
  },
];

export default function PrivacyPolicyPage() {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  useEffect(() => {
    const PAGE_TITLE = 'Privacy Policy – EVENTSHOWCASE';
    const PAGE_DESC = 'Read the EVENTSHOWCASE Privacy Policy. Learn how we collect, use, and protect your personal data, including our use of Google AdSense advertising cookies.';
    const PAGE_URL = `${SITE_URL}/legal/privacy-policy`;

    document.title = PAGE_TITLE;

    const setMeta = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const [a, v] = sel.replace('meta[', '').replace(']', '').split('=');
        el.setAttribute(a.trim(), v.replace(/"/g, '').trim());
        document.head.appendChild(el);
        el.setAttribute('data-page-meta', 'true');
      }
      el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', PAGE_DESC);
    setMeta('meta[name="robots"]', 'content', 'index, follow');
    setMeta('meta[property="og:title"]', 'content', PAGE_TITLE);
    setMeta('meta[property="og:description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:url"]', 'content', PAGE_URL);
    setMeta('meta[property="og:type"]', 'content', 'website');

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy – EVENTSHOWCASE',
      description: PAGE_DESC,
      url: PAGE_URL,
      dateModified: '2026-04-05',
      isPartOf: { '@type': 'WebSite', name: 'EVENTSHOWCASE', url: SITE_URL },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Legal', item: `${SITE_URL}/legal` },
          { '@type': 'ListItem', position: 3, name: 'Privacy Policy', item: PAGE_URL },
        ],
      },
    };

    const existing = document.getElementById('schema-privacy');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-privacy';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-privacy');
      if (el) el.remove();
      document.querySelectorAll('meta[data-page-meta="true"]').forEach(e => e.remove());
    };
  }, []);

  const renderContent = (content: string) => {
    return content.split('\n\n').map((paragraph, i) => {
      if (paragraph.startsWith('**') && paragraph.endsWith('**') && !paragraph.includes('\n')) {
        return (
          <h4 key={i} className="font-semibold text-gray-900 mt-5 mb-2 text-sm">
            {paragraph.replace(/\*\*/g, '')}
          </h4>
        );
      }
      const parts = paragraph.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
      return (
        <p key={i} className="text-gray-600 text-sm leading-relaxed mb-3">
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j} className="text-gray-800 font-semibold">{part.slice(2, -2)}</strong>;
            }
            if (part.startsWith('*') && part.endsWith('*')) {
              return <em key={j} className="text-gray-700 font-medium not-italic">{part.slice(1, -1)}</em>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/">
            <img
              src="https://public.readdy.ai/ai/img_res/35af317c-ff06-4831-b241-787dd1236a773.png"
              alt="EVENTSHOWCASE"
              className="h-8 w-auto"
            />
          </Link>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <Link to="/legal/refund-policy" className="hover:text-gray-900 transition-colors">Refund Policy</Link>
            <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-gray-950 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-medium px-3 py-1.5 rounded-full mb-6">
            <i className="ri-shield-check-line text-sm"></i>
            Legal Document
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4 font-['Playfair_Display']">Privacy Policy</h1>
          <p className="text-gray-400 text-sm sm:text-base max-w-2xl">
            This policy describes how EVENTSHOWCASE collects, uses, and protects your personal information, including our use of Google AdSense advertising services.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-6 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              Last Updated: April 5, 2026
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-global-line"></i>
              Applies to: eventshowcase.de
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-file-text-line"></i>
              GDPR &amp; CCPA Compliant
            </span>
          </div>
        </div>
      </div>

      {/* AdSense Notice Banner */}
      <div className="bg-amber-50 border-b border-amber-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-advertisement-line text-amber-600 text-lg"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-800">Advertising Disclosure</p>
            <p className="text-xs text-amber-700 mt-0.5">
              This website uses Google AdSense to display advertisements. Google may use cookies to serve ads based on your prior visits to this and other websites.
              You can opt out at <a href="https://www.google.com/settings/ads" target="_blank" rel="nofollow noopener noreferrer" className="underline hover:text-amber-900">google.com/settings/ads</a>.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <div className="flex flex-col lg:flex-row gap-10">

          {/* Table of Contents */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="lg:sticky lg:top-24">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={() => setActiveSection(section.id)}
                    className={`block text-xs py-1.5 px-3 rounded-md transition-colors cursor-pointer ${
                      activeSection === section.id
                        ? 'bg-gray-900 text-white font-medium'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {section.title}
                  </a>
                ))}
              </nav>

              <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <p className="text-xs font-semibold text-gray-700 mb-2">Quick Links</p>
                <div className="space-y-2">
                  <a
                    href="https://www.google.com/settings/ads"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
                  >
                    <i className="ri-external-link-line text-xs"></i>
                    Google Ads Settings
                  </a>
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
                  >
                    <i className="ri-external-link-line text-xs"></i>
                    Google Privacy Policy
                  </a>
                  <a
                    href="http://www.networkadvertising.org/managing/opt_out.asp"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
                  >
                    <i className="ri-external-link-line text-xs"></i>
                    NAI Opt-Out
                  </a>
                  <a
                    href="https://www.aboutads.info"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
                  >
                    <i className="ri-external-link-line text-xs"></i>
                    AboutAds.info
                  </a>
                </div>
              </div>
            </div>
          </aside>

          {/* Policy Sections */}
          <main className="flex-1 min-w-0">
            <div className="space-y-10">
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-24">
                  <h2 className="text-base font-bold text-gray-900 mb-4 pb-3 border-b border-gray-100">
                    {section.title}
                  </h2>
                  <div>{renderContent(section.content)}</div>
                </section>
              ))}
            </div>

            {/* Footer note */}
            <div className="mt-12 p-6 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-gray-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 mb-1">Questions about this policy?</p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    If you have any questions about how we handle your data or our use of Google AdSense, please reach out to us at{' '}
                    <a href="mailto:privacy@eventshowcase.de" className="text-gray-800 underline hover:no-underline">
                      privacy@eventshowcase.de
                    </a>. We aim to respond within 5 business days.
                  </p>
                </div>
              </div>
            </div>

            {/* Related policies */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link
                to="/legal/refund-policy"
                className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-gray-300 transition-colors cursor-pointer group"
              >
                <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors">
                  <i className="ri-refund-2-line text-gray-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Refund &amp; Tax Policy</p>
                  <p className="text-xs text-gray-400">Payment &amp; refund terms</p>
                </div>
                <i className="ri-arrow-right-line text-gray-400 ml-auto"></i>
              </Link>
              <Link
                to="/"
                className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-gray-300 transition-colors cursor-pointer group"
              >
                <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors">
                  <i className="ri-home-4-line text-gray-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Back to Home</p>
                  <p className="text-xs text-gray-400">Return to EVENTSHOWCASE</p>
                </div>
                <i className="ri-arrow-right-line text-gray-400 ml-auto"></i>
              </Link>
            </div>
          </main>
        </div>
      </div>

      {/* Footer strip */}
      <div className="border-t border-gray-100 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-400">
          <p>&copy; 2026 EVENTSHOWCASE. All rights reserved.</p>
          <div className="flex items-center gap-3">
            <Link to="/legal/privacy-policy" className="text-gray-700 font-medium">Privacy Policy</Link>
            <span>·</span>
            <Link to="/legal/refund-policy" className="hover:text-gray-700 transition-colors">Refund Policy</Link>
            <span>·</span>
            <Link to="/" className="hover:text-gray-700 transition-colors">Home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
